import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import {
  formatDeliveryDays,
  generatePortalToken,
  getDeliveryScheduleIssues,
  joinCustomerEmails,
  parseCustomerEmails,
  parseNavDeliverySchedule,
  portalOrderUrl,
  updateDemCustomer,
} from '../../lib/demCustomers';
import type { DemCustomer } from '../../types/dem';

const DAY_OPTIONS = [
  { idx: 0, label: 'Mon' },
  { idx: 1, label: 'Tue' },
  { idx: 2, label: 'Wed' },
  { idx: 3, label: 'Thu' },
  { idx: 4, label: 'Fri' },
  { idx: 5, label: 'Sat' },
  { idx: 6, label: 'Sun' },
] as const;

type CustomerDetailModalProps = {
  customer: DemCustomer | null;
  open: boolean;
  onClose: () => void;
  onSaved: (customer: DemCustomer) => void;
};

export function CustomerDetailModal({ customer, open, onClose, onSaved }: CustomerDetailModalProps) {
  const [days, setDays] = useState<number[]>([]);
  const [minOrder, setMinOrder] = useState('0.00');
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [portalToken, setPortalToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!customer || !open) return;
    // Prefer PowerBI / NAV schedule text when it parses cleanly (e.g. "TUES, THURS").
    const fromNav = parseNavDeliverySchedule(customer.delivery_schedule);
    setDays(fromNav ?? [...customer.allowed_days]);
    setMinOrder(customer.min_order.toFixed(2));
    setEmails(parseCustomerEmails(customer.email));
    setPortalToken(customer.portal_token);
    setNewEmail('');
    setError(null);
    setCopied(false);
  }, [customer, open]);

  if (!customer) return null;

  const scheduleIssues = getDeliveryScheduleIssues(
    customer.delivery_schedule,
    customer.allowed_days,
  );
  const parsedFromNav = parseNavDeliverySchedule(customer.delivery_schedule);

  const toggleDay = (idx: number) => {
    setDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort((a, b) => a - b),
    );
  };

  const addEmail = () => {
    const val = newEmail.trim();
    if (!val) return;
    if (!val.includes('@') || !val.includes('.')) {
      setError('Please enter a valid email address.');
      return;
    }
    if (emails.includes(val)) {
      setError('This email is already in the list.');
      return;
    }
    setEmails((prev) => [...prev, val]);
    setNewEmail('');
    setError(null);
  };

  const removeEmail = (email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  };

  const ensureToken = () => {
    if (portalToken) return portalToken;
    const token = generatePortalToken();
    setPortalToken(token);
    return token;
  };

  const copyPortalLink = async () => {
    const token = ensureToken();
    const url = portalOrderUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const rawMoq = minOrder.replace(/[$,]/g, '').trim();
      const parsedMoq = rawMoq ? Number(rawMoq) : 0;
      if (!Number.isFinite(parsedMoq)) {
        setError('Minimum order amount must be a valid number.');
        setSaving(false);
        return;
      }

      const patch: Parameters<typeof updateDemCustomer>[1] = {
        allowed_days: days,
        email: joinCustomerEmails(emails),
        min_order: parsedMoq,
      };
      if (portalToken && portalToken !== customer.portal_token) {
        patch.portal_token = portalToken;
      }

      const updated = await updateDemCustomer(customer.code, patch);
      onSaved(updated);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save customer.');
    } finally {
      setSaving(false);
    }
  };

  const portalLink = portalToken ? portalOrderUrl(portalToken) : '';

  return (
    <Modal
      title="Customer settings"
      open={open}
      onClose={onClose}
      wide
    >
      <div className="dem-customer-modal">
        <p className="dem-customer-modal__subtitle">
          [{customer.code}] {customer.name}
          {customer.is_blocked ? ' · BLOCKED' : ''}
        </p>

        <section className="dem-customer-modal__section">
          <h3 className="dem-customer-modal__heading">Delivery instruction</h3>
          <p className="dem-customer-modal__readonly">
            {customer.delivery_instruction.trim() || '—'}
          </p>
        </section>

        <section className="dem-customer-modal__section">
          <h3 className="dem-customer-modal__heading">Delivery schedule (PowerBI / NAV)</h3>
          <p className="dem-customer-modal__readonly">
            {customer.delivery_schedule.trim() || '—'}
          </p>
          {scheduleIssues.length > 0 ? (
            <ul className="dem-customer-modal__schedule-issues">
              {scheduleIssues.map((issue) => (
                <li key={`${issue.kind}:${issue.label}`}>{issue.label}</li>
              ))}
            </ul>
          ) : null}
        </section>

        <section className="dem-customer-modal__section">
          <h3 className="dem-customer-modal__heading">Delivery schedule</h3>
          <p className="dem-customer-modal__hint">
            Paired from PowerBI / NAV
            {customer.delivery_schedule.trim()
              ? ` (“${customer.delivery_schedule.trim()}”)`
              : ' when available'}
            {parsedFromNav != null ? ` → ${formatDeliveryDays(parsedFromNav)}` : ''}
            . DAILY = Mon–Fri. You can still adjust before saving.
          </p>
          <div className="dem-day-pills">
            {DAY_OPTIONS.map(({ idx, label }) => (
              <button
                key={idx}
                type="button"
                className={`dem-day-pill${days.includes(idx) ? ' dem-day-pill--on' : ''}`}
                onClick={() => toggleDay(idx)}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        <section className="dem-customer-modal__section">
          <h3 className="dem-customer-modal__heading">Minimum order (SGD)</h3>
          <input
            className="field-input"
            type="text"
            inputMode="decimal"
            value={minOrder}
            onChange={(e) => setMinOrder(e.target.value)}
          />
        </section>

        <section className="dem-customer-modal__section">
          <h3 className="dem-customer-modal__heading">B2B portal link</h3>
          <div className="dem-portal-link-row">
            <input className="field-input" type="text" readOnly value={portalLink || 'Use Copy URL to create a link.'} />
            <button type="button" className="btn-secondary btn-sm" onClick={() => void copyPortalLink()}>
              {copied ? 'Copied' : 'Copy URL'}
            </button>
          </div>
        </section>

        <section className="dem-customer-modal__section">
          <h3 className="dem-customer-modal__heading">Notification emails</h3>
          <div className="dem-email-add-row">
            <input
              className="field-input"
              type="email"
              placeholder="Enter email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addEmail();
                }
              }}
            />
            <button type="button" className="btn-secondary btn-sm" onClick={addEmail}>
              Add
            </button>
          </div>
          {emails.length > 0 ? (
            <ul className="dem-email-list">
              {emails.map((email) => (
                <li key={email}>
                  <span>{email}</span>
                  <button type="button" className="btn-secondary btn-sm dem-email-remove" onClick={() => removeEmail(email)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="page-lead">No notification emails yet.</p>
          )}
        </section>

        {error && <p className="alert alert-error">{error}</p>}

        <div className="dem-customer-modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save all'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
